import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";

import { toast } from "react-toastify";

import { documentsActionStore } from "../documentActionStores";
import { uploadPdfStore } from "../UploadPdf/uploadPdfStore";

import {
  ChevronRight,
  FileSearch2,
  FileText,
  Loader2,
  Search,
  Trash2,
} from "lucide-react";

import {
  getAllDocuments,
  getChunksByDocument,
} from "../../Apis_chatbot/apis";

import {
  bulkEditDocumentChunks,
  retryEditPendingDocument,
  retryPendingDocument,
  deleteDocument,
  retryDeleteDocument,
} from "../../Api_Qdrant/Fast_apis";
import { getFastApiErrorMessage } from "../../Api_Qdrant/fastApiError";
import ConfirmDialog from "../../app/components/ConfirmDialog/ConfirmDialog";

import "./Documents.css";

const formatDate = (value) => {
  if (!value) return "-";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "-";
  }

  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const normalizeText = (text) =>
  typeof text === "string"
    ? text.trim()
    : "";

// Grow a chunk <textarea> to fit its content so the editor never becomes a
// tiny scroll box (matters most on phones / small screens).
const autoSizeTextarea = (el) => {
  if (!el) return;

  el.style.height = "auto";

  // scrollHeight is content + padding; with box-sizing: border-box the
  // element height also needs the borders, so add them back.
  const borders = el.offsetHeight - el.clientHeight;

  el.style.height = `${el.scrollHeight + borders}px`;
};

const retryableStatuses = new Set([
  "pending",
  "edit_pending",
]);

// Retries and chunk edits are applied by the backend asynchronously: the
// document stays in "pending" / "edit_pending" after the request returns and
// flips to "completed" on its own, with nothing pushing that change to the
// client. While a document is in that state we re-poll the list on this
// cadence, capped so a genuinely stuck document doesn't poll forever.
const STATUS_POLL_INTERVAL_MS = 4000;
const MAX_STATUS_POLLS = 45; // ~3 minutes

// A per-chunk bulk-edit result only counts as a failure when it carries one of
// these markers. Chunk edits are applied asynchronously, so other values
// ("completed", "queued", "edit_pending", …) all mean the change was accepted.
const FAILED_CHUNK_STATUSES = new Set([
  "failed",
  "error",
  "errored",
  "rejected",
  "skipped",
]);

const DELETE_PENDING_STATUS =
  "delete_pending";

const QDRANT_NOT_SYNCED_MESSAGE =
  "The document was removed from the application, " +
  "but cleanup is still pending. Please retry deletion.";

const formatStatusLabel = (status) =>
  String(status || "unknown")
    .replace(/_/g, " ")
    .replace(
      /\b\w/g,
      (char) => char.toUpperCase()
    );

// Frontend-controlled error copy for FastAPI calls — never surfaces backend
// text. See Api_Qdrant/fastApiError.js.
const getSafeErrorMessage = getFastApiErrorMessage;

function Documents() {
  const [documents, setDocuments] =
    useState([]);

  const [selectedDocId, setSelectedDocId] =
    useState("");

  const [chunks, setChunks] =
    useState([]);

  const [editableChunks, setEditableChunks] =
    useState([]);

  const [docsLoading, setDocsLoading] =
    useState(true);

  const [chunksLoading, setChunksLoading] =
    useState(false);

  // Retry / delete / bulk-edit progress lives in a module-level store so the
  // button loading state is not lost if the user switches tabs mid-request and
  // comes back before it finishes.
  const { retryingDocId, deletingDocId, editingDocId } = useSyncExternalStore(
    documentsActionStore.subscribe,
    documentsActionStore.getSnapshot
  );

  const setRetryingDocId = (id) =>
    documentsActionStore.set("retryingDocId", id);

  const setDeletingDocId = (id) =>
    documentsActionStore.set("deletingDocId", id);

  // Set to the document id while its chunk bulk-edit request (and the refresh
  // that follows) is in flight.
  const setEditingDocId = (id) =>
    documentsActionStore.set("editingDocId", id);

  // A PDF upload started from the Upload tab may still be running when the user
  // comes here. While it is, the freshly-created document sits in "pending" —
  // retrying it now just races the upload, so retry stays disabled until the
  // upload request returns.
  const uploadSnapshot = useSyncExternalStore(
    uploadPdfStore.subscribe,
    uploadPdfStore.getSnapshot
  );

  const isUploadInProgress = Boolean(
    uploadSnapshot?.loading
  );

  const [savingEdits, setSavingEdits] =
    useState(false);

  const [isEditMode, setIsEditMode] =
    useState(false);

  const [searchText, setSearchText] =
    useState("");

  const [error, setError] =
    useState("");

  const [chunkTokenErrors, setChunkTokenErrors] =
    useState({});

  // Document pending a delete confirmation in the popup.
  const [deleteTarget, setDeleteTarget] =
    useState(null);

  const chunksScrollRef = useRef(null);

  const selectedDocument = useMemo(
    () =>
      documents.find(
        (doc) =>
          doc.qdrantDocId ===
          selectedDocId
      ) || null,
    [
      documents,
      selectedDocId,
    ]
  );

  const selectedStatusKey = (
    selectedDocument?.status || ""
  ).toLowerCase();

  // True while any row action (retry / delete / retry-delete) is in flight for
  // the given document id. Every action button for that document — and edit
  // mode, when it is the selected one — must stay disabled until it settles.
  const isDocBusy = (docId) =>
    Boolean(docId) &&
    (retryingDocId === docId || deletingDocId === docId);

  // A retry request for the selected document is currently in flight.
  const isSelectedDocRetrying =
    Boolean(selectedDocument?.qdrantDocId) &&
    retryingDocId === selectedDocument.qdrantDocId;

  // A delete / retry-delete request for the selected document is in flight.
  const isSelectedDocDeleting =
    Boolean(selectedDocument?.qdrantDocId) &&
    deletingDocId === selectedDocument.qdrantDocId;

  // A chunk bulk-edit request is in flight for the given / selected document.
  // While it is, the document is on its way to "edit_pending" and a retry now
  // would race it — mirrors how retry waits out an in-progress upload.
  const isDocEditing = (docId) =>
    Boolean(docId) && editingDocId === docId;

  const isSelectedDocEditing =
    Boolean(selectedDocument?.qdrantDocId) &&
    editingDocId === selectedDocument.qdrantDocId;

  // Any row action is in flight for the selected document.
  const isSelectedDocBusy =
    isSelectedDocRetrying || isSelectedDocDeleting;

  // The selected document has not finished processing yet
  // (pending / edit_pending) so its chunks must not be edited.
  const isSelectedDocRetryable =
    retryableStatuses.has(selectedStatusKey);

  // Edit mode must stay locked while the document is retrying, deleting,
  // still pending processing, or pending deletion.
  const isEditModeLocked =
    chunksLoading ||
    savingEdits ||
    isSelectedDocRetrying ||
    isSelectedDocDeleting ||
    isSelectedDocRetryable ||
    selectedStatusKey === DELETE_PENDING_STATUS;

  // ==========================================================
  // LOAD DOCUMENTS
  // ==========================================================

  // `silent` is used by the background status poller: it refreshes the list
  // in place without the loading spinner, and a transient failure leaves the
  // current list untouched instead of wiping the page.
  const loadDocuments = async (
    preferredSelectedDocId = null,
    { silent = false } = {}
  ) => {
    if (!silent) {
      setDocsLoading(true);
      setError("");
    }

    try {
      const res =
        await getAllDocuments();

      const status =
        res?.status_code ??
        res?.status ??
        null;

      const hasError =
        res?.success === false ||
        Boolean(res?.error) ||
        Boolean(res?.error_value) ||
        Boolean(res?.error_code) ||
        (status !== null &&
          Number(status) >= 400);

      if (hasError) {
        if (silent) return;

        const userMessage =
          getSafeErrorMessage(
            res,
            "We could not load documents right now. Please try again."
          );

        setDocuments([]);
        setSelectedDocId("");
        setChunks([]);
        setEditableChunks([]);

        setError(userMessage);
        toast.error(userMessage);

        return;
      }

      const list =
        Array.isArray(
          res?.result_value
        )
          ? res.result_value
          : [];

      setDocuments(list);

      if (list.length > 0) {
        const preferredExists =
          preferredSelectedDocId &&
          list.some(
            (doc) =>
              doc.qdrantDocId ===
              preferredSelectedDocId
          );

        setSelectedDocId(
          preferredExists
            ? preferredSelectedDocId
            : list[0].qdrantDocId
        );
      } else {
        setSelectedDocId("");
        setChunks([]);
        setEditableChunks([]);
      }
    } catch (err) {
      console.error(
        "Failed to load documents:",
        err
      );

      if (silent) return;

      const userMessage =
        getSafeErrorMessage(
          err,
          "We could not load documents right now. Please try again."
        );

      setDocuments([]);
      setError(userMessage);

      toast.error(userMessage);
    } finally {
      if (!silent) {
        setDocsLoading(false);
      }
    }
  };

  // ==========================================================
  // LOAD CHUNKS
  // ==========================================================

  const loadChunks = async (
    docId
  ) => {
    if (!docId) return;

    setChunksLoading(true);
    setError("");
    setChunkTokenErrors({});

    try {
      const res =
        await getChunksByDocument(
          docId
        );

      const status =
        res?.status_code ??
        res?.status ??
        null;

      const hasError =
        res?.success === false ||
        Boolean(res?.error) ||
        Boolean(res?.error_value) ||
        Boolean(res?.error_code) ||
        (status !== null &&
          Number(status) >= 400);

      if (hasError) {
        const userMessage =
          getSafeErrorMessage(
            res,
            "We could not load the chunks for this document. Please try again."
          );

        setChunks([]);
        setEditableChunks([]);
        setError(userMessage);

        toast.error(userMessage);

        return;
      }

      const list =
        Array.isArray(
          res?.result_value
        )
          ? res.result_value
          : [];

      setChunks(list);

      setEditableChunks(
        list.map((chunk) => ({
          ...chunk,
          chunkText:
            chunk.chunkText || "",
        }))
      );

      setIsEditMode(false);
    } catch (err) {
      console.error(
        "Failed to load chunks:",
        err
      );

      setChunks([]);
      setEditableChunks([]);

      const userMessage =
        getSafeErrorMessage(
          err,
          "We could not load the chunks for this document. Please try again."
        );

      setError(userMessage);
      toast.error(userMessage);
    } finally {
      setChunksLoading(false);
    }
  };

  // ==========================================================
  // RETRY DOCUMENT
  // ==========================================================

  const handleRetry = async (
    doc
  ) => {
    const docId =
      doc?.qdrantDocId;

    if (!docId) {
      toast.error(
        "The document could not be identified. Please refresh the page."
      );

      return;
    }

    // Another action is already running for this document.
    if (isDocBusy(docId)) return;

    // A PDF upload is still in flight — retrying now would race it.
    if (isUploadInProgress) {
      toast.info(
        "Please wait for the current upload to finish before retrying."
      );

      return;
    }

    // A chunk bulk-edit for this document is still in flight — retrying now
    // would race it.
    if (isDocEditing(docId)) {
      toast.info(
        "Please wait for the current chunk edit to finish before retrying."
      );

      return;
    }

    const statusKey = (
      doc?.status || ""
    ).toLowerCase();

    const retryFn =
      statusKey === "edit_pending"
        ? retryEditPendingDocument
        : retryPendingDocument;

    setRetryingDocId(docId);

    try {
      const res =
        await retryFn(docId);

      // Rate-limited interceptor already handled it.
      if (res?.rateLimited) {
        return;
      }

      const status =
        res?.status_code ??
        res?.status ??
        null;

      const hasError =
        res?.success === false ||
        Boolean(res?.error) ||
        Boolean(res?.error_value) ||
        Boolean(res?.error_code) ||
        (status !== null &&
          Number(status) >= 400);

      if (hasError) {
        const userMessage =
          getSafeErrorMessage(
            res,
            "The document could not be retried. Please try again."
          );

        toast.error(userMessage);

        return;
      }

      toast.success(
        statusKey === "edit_pending"
          ? "Edit retry started successfully."
          : "Retry started successfully."
      );

      await loadDocuments(
        selectedDocId
      );

      if (
        docId === selectedDocId
      ) {
        await loadChunks(
          docId
        );
      }
    } catch (err) {
      console.error(
        "Retry failed:",
        err
      );

      const userMessage =
        getSafeErrorMessage(
          err,
          "The document could not be retried. Please try again."
        );

      toast.error(userMessage);
    } finally {
      setRetryingDocId("");
    }
  };

  // ==========================================================
  // DELETE RESPONSE
  // ==========================================================

  const handleDeleteResponse =
    async (
      res,
      docId
    ) => {
      if (res?.rateLimited) {
        return;
      }

      const status =
        res?.status_code ??
        res?.status ??
        null;

      const hasError =
        res?.success === false ||
        Boolean(res?.error) ||
        Boolean(res?.error_value) ||
        Boolean(res?.error_code) ||
        (status !== null &&
          Number(status) >= 400);

      if (hasError) {
        const userMessage =
          getSafeErrorMessage(
            res,
            "The document could not be deleted. Please try again."
          );

        toast.error(
          userMessage
        );

        return;
      }

      const isFullyDeleted =
        res?.vector_store_synced !== false &&
        res?.deletion_confirmed !== false;

      if (!isFullyDeleted) {
        toast.error(
          QDRANT_NOT_SYNCED_MESSAGE
        );
      } else {
        toast.success(
          "Document deleted successfully."
        );
      }

      const nextSelectedId =
        docId === selectedDocId
          ? null
          : selectedDocId;

      await loadDocuments(
        nextSelectedId
      );
    };

  // ==========================================================
  // DELETE DOCUMENT
  // ==========================================================

  // Opens the delete confirmation popup.
  const handleDelete = (doc) => {
    const docId = doc?.qdrantDocId;

    if (!docId) {
      toast.error(
        "The document could not be identified. Please refresh the page."
      );

      return;
    }

    // Another action is already running for this document.
    if (isDocBusy(docId)) return;

    setDeleteTarget(doc);
  };

  // Runs after the user confirms in the popup.
  const confirmDelete = async () => {
    const doc = deleteTarget;
    const docId = doc?.qdrantDocId;

    if (!docId) {
      setDeleteTarget(null);
      return;
    }

    setDeletingDocId(docId);

    try {
      const res =
        await deleteDocument(
          docId
        );

      await handleDeleteResponse(
        res,
        docId
      );
    } catch (err) {
      console.error(
        "Delete failed:",
        err
      );

      const userMessage =
        getSafeErrorMessage(
          err,
          "The document could not be deleted. Please try again."
        );

      toast.error(
        userMessage
      );
    } finally {
      setDeletingDocId("");
      setDeleteTarget(null);
    }
  };

  // ==========================================================
  // RETRY DELETE
  // ==========================================================

  const handleRetryDelete =
    async (
      doc
    ) => {
      const docId =
        doc?.qdrantDocId;

      if (!docId) {
        toast.error(
          "The document could not be identified. Please refresh the page."
        );

        return;
      }

      // Another action is already running for this document.
      if (isDocBusy(docId)) return;

      setDeletingDocId(
        docId
      );

      try {
        const res =
          await retryDeleteDocument(
            docId
          );

        await handleDeleteResponse(
          res,
          docId
        );
      } catch (err) {
        console.error(
          "Retry delete failed:",
          err
        );

        const userMessage =
          getSafeErrorMessage(
            err,
            "The document could not be deleted. Please try again."
          );

        toast.error(
          userMessage
        );
      } finally {
        setDeletingDocId("");
      }
    };

  // ==========================================================
  // TOGGLE EDIT MODE
  // ==========================================================

  const handleToggleEditMode =
    () => {
      setChunkTokenErrors({});

      if (isEditMode) {
        setEditableChunks(
          chunks.map(
            (chunk) => ({
              ...chunk,
              chunkText:
                chunk.chunkText ||
                "",
            })
          )
        );

        setIsEditMode(false);

        return;
      }

      // Never enter edit mode while it is locked — document retrying /
      // deleting, still pending processing, deletion pending, or chunks
      // still loading / saving.
      if (isEditModeLocked) return;

      setEditableChunks(
        chunks.map(
          (chunk) => ({
            ...chunk,
            chunkText:
              chunk.chunkText ||
              "",
          })
        )
      );

      setIsEditMode(true);
    };

  // ==========================================================
  // CHUNK TEXT CHANGE
  // ==========================================================

  const handleChunkTextChange =
    (
      index,
      value
    ) => {
      setEditableChunks(
        (prev) =>
          prev.map(
            (
              chunk,
              chunkIndex
            ) =>
              chunkIndex === index
                ? {
                    ...chunk,
                    chunkText:
                      value,
                  }
                : chunk
          )
      );

      const changedChunkId =
        editableChunks[
          index
        ]?.qdrantChunkId;

      if (changedChunkId) {
        setChunkTokenErrors(
          (prev) => {
            if (
              !prev[
                changedChunkId
              ]
            ) {
              return prev;
            }

            const next = {
              ...prev,
            };

            delete next[
              changedChunkId
            ];

            return next;
          }
        );
      }
    };

  // ==========================================================
  // BULK EDIT
  // ==========================================================

  const handleSaveBulkEdit =
    async () => {
      if (
        !selectedDocument?.qdrantDocId
      ) {
        toast.error(
          "The selected document could not be identified. Please refresh the page."
        );

        return;
      }

      const originalTextById =
        new Map(
          chunks.map(
            (chunk) => [
              chunk.qdrantChunkId,
              chunk.chunkText ||
                "",
            ]
          )
        );

      const changedChunks =
        editableChunks.filter(
          (chunk) =>
            (chunk.chunkText ||
              "") !==
            (
              originalTextById.get(
                chunk.qdrantChunkId
              ) || ""
            )
        );

      if (
        changedChunks.length ===
        0
      ) {
        toast.info(
          "No chunk changes to save."
        );

        return;
      }

      setSavingEdits(true);
      // Flag the document as "chunks updating" in the module store so its Retry
      // button shows "Processing" (and stays disabled) until the edit request —
      // and the refresh that follows — settle, even across a tab switch.
      setEditingDocId(selectedDocument.qdrantDocId);
      setChunkTokenErrors({});

      // When true, keep the user in edit mode with their drafts intact — the
      // request never reached the server (rate limited) or was rejected for
      // oversized chunks they still need to fix. Every other outcome, success
      // or failure, refreshes the view because a failed edit usually changes
      // the document status server-side.
      let keepEditing = false;

      // True only on a clean success. The backend moves the document to
      // "edit_pending" and re-embeds the chunks in the background, so the
      // "Processing" flag is kept past this request and cleared later (by the
      // status poller / auto-clear effect) once it flips back to "completed".
      let editAccepted = false;

      try {
        const payload = {
          qdrantDocId:
            selectedDocument.qdrantDocId,

          chunks:
            changedChunks.map(
              (chunk) => ({
                qdrantChunkId:
                  chunk.qdrantChunkId,

                chunkText:
                  chunk.chunkText ||
                  "",
              })
            ),
        };

        const res =
          await bulkEditDocumentChunks(
            payload
          );

        // Rate limited.
        if (res?.rateLimited) {
          keepEditing = true;
          return;
        }

        const status =
          res?.status_code ??
          res?.status ??
          null;

        const hasError =
          res?.success === false ||
          Boolean(res?.error) ||
          Boolean(res?.error_value) ||
          Boolean(res?.error_code) ||
          (status !== null &&
            Number(status) >= 400);

        if (hasError) {
          // --------------------------------------------------
          // TOKEN LIMIT ERROR
          //
          // We use structured fields from the response,
          // but NOT the backend message.
          // --------------------------------------------------

          const overLimitChunks =
            Array.isArray(
              res?.errors?.chunks
            )
              ? res.errors.chunks
              : [];

          if (
            res?.errors?.reason ===
              "CHUNK_TOKEN_LIMIT_EXCEEDED" &&
            overLimitChunks.length >
              0
          ) {
            // Each over-limit chunk reports its OWN cap in
            // `item.max_tokens`: the backend limits plain text, a
            // table-only chunk, and a table with surrounding text
            // differently. Older responses sent only a single
            // response-level `max_tokens`, so fall back to that.
            const fallbackMaxTokens =
              Number(
                res?.errors?.max_tokens
              ) || null;

            const tableOnlyMaxTokens =
              Number(
                res?.errors
                  ?.table_only_max_tokens
              ) || null;

            const tableTextMaxTokens =
              Number(
                res?.errors
                  ?.table_with_text_max_tokens
              ) || null;



            setChunkTokenErrors(
              overLimitChunks.reduce(
                (
                  acc,
                  item
                ) => {
                  if (
                    item?.chunk_id
                  ) {
                    const maxTokens =
                      Number(
                        item.max_tokens
                      ) ||
                      fallbackMaxTokens ||
                      null;

                    acc[
                      item.chunk_id
                    ] = {
                      tokenCount:
                        Number(
                          item.token_count
                        ) ||
                        null,

                      maxTokens,
                    };
                  }

                  return acc;
                },
                {}
              )
            );

            toast.error(
              "One or more chunks exceed their token limit. please shorten the content and try again."
            );

            keepEditing = true;
            return;
          }

          // --------------------------------------------------
          // OTHER BACKEND ERROR
          // --------------------------------------------------

          const userMessage =
            getSafeErrorMessage(
              res,
              "The document chunks could not be saved. Please try again."
            );

          toast.error(
            userMessage
          );

          return;
        }

        // ====================================================
        // CHECK FOR FAILED CHUNKS
        // ====================================================

        const results =
          Array.isArray(res?.results)
            ? res.results
            : [];

        const failedChunkCount =
          results.filter((item) =>
            FAILED_CHUNK_STATUSES.has(
              String(item?.status || "").toLowerCase()
            )
          ).length;

        const allChunksFailed =
          results.length > 0 &&
          failedChunkCount === results.length;

        if (allChunksFailed) {
          toast.error(
            "The document chunks could not be saved. Please try again."
          );

          return;
        }

        if (failedChunkCount > 0) {
          toast.error(
            "Some chunks were not saved successfully. Please try again."
          );
        } else {
          editAccepted = true;

          toast.success(
            "Document chunks updated successfully."
          );
        }
      } catch (err) {
        console.error(
          "Bulk edit failed:",
          err
        );

        const userMessage =
          getSafeErrorMessage(
            err,
            "The document chunks could not be saved. Please try again."
          );

        toast.error(
          userMessage
        );
      } finally {
        // Resync the document list and this document's chunks with the
        // server after any save attempt that reached it — successes AND
        // failures, since a failed edit usually flips the document status
        // (e.g. to "edit_pending"). Do this before dropping `savingEdits` so
        // the auto-clear effect below reads the fresh status, not the stale
        // pre-request one.
        if (!keepEditing) {
          setIsEditMode(false);
          setChunkTokenErrors({});

          await loadDocuments(
            selectedDocument.qdrantDocId
          );

          await loadChunks(
            selectedDocument.qdrantDocId
          );
        }

        setSavingEdits(false);

        // On a clean success the document is now in "edit_pending" while the
        // backend re-embeds the chunks; keep the "Processing" flag on until
        // the status poller sees it flip back to "completed" (handled by the
        // clear-editing effect). Every other outcome clears it now so the
        // real status — and its Retry button — show immediately.
        if (!editAccepted) {
          setEditingDocId("");
        }
      }
    };

  // ==========================================================
  // INITIAL LOAD
  // ==========================================================

  useEffect(() => {
    // Drop any retry/delete flag left over from a request that never
    // settled before a previous unmount.
    documentsActionStore.pruneStale();

    loadDocuments();
  }, []);

  useEffect(() => {
    if (selectedDocId) {
      loadChunks(
        selectedDocId
      );
    }
  }, [selectedDocId]);

  // ==========================================================
  // STATUS POLLING
  //
  // While any document is still processing (pending / edit_pending), keep
  // pulling a fresh list silently so its status — and its Retry button —
  // update on their own once the backend finishes, without the user having
  // to leave the tab and come back.
  // ==========================================================

  const hasProcessingDoc = useMemo(
    () =>
      documents.some((doc) =>
        retryableStatuses.has(
          String(doc.status || "").toLowerCase()
        )
      ),
    [documents]
  );

  // Read inside the interval without restarting it when the selection changes.
  const selectedDocIdRef = useRef(selectedDocId);

  useEffect(() => {
    selectedDocIdRef.current = selectedDocId;
  }, [selectedDocId]);

  useEffect(() => {
    if (!hasProcessingDoc) return;

    let polls = 0;

    const timer = setInterval(() => {
      polls += 1;

      if (polls > MAX_STATUS_POLLS) {
        clearInterval(timer);
        return;
      }

      loadDocuments(selectedDocIdRef.current, {
        silent: true,
      });
    }, STATUS_POLL_INTERVAL_MS);

    return () => clearInterval(timer);
  }, [hasProcessingDoc]);

  // The bulk-edit "Processing" flag is deliberately kept on past a successful
  // save (the backend is still re-embedding). Clear it once the polled status
  // shows the document has left "edit_pending" — or the document is gone.
  // Guarded so it can't fire on the stale pre-request status or before the
  // first list load has landed.
  useEffect(() => {
    if (
      !editingDocId ||
      savingEdits ||
      docsLoading ||
      documents.length === 0
    ) {
      return;
    }

    const doc = documents.find(
      (item) => item.qdrantDocId === editingDocId
    );

    const stillProcessing =
      doc &&
      String(doc.status || "").toLowerCase() ===
        "edit_pending";

    if (!stillProcessing) {
      setEditingDocId("");
    }
  }, [
    documents,
    editingDocId,
    savingEdits,
    docsLoading,
  ]);

  // If the selected document starts retrying / deleting, or is otherwise no
  // longer editable, close edit mode and drop any unsaved chunk drafts so the
  // UI can't sit in a locked-but-open edit state.
  useEffect(() => {
    if (!isEditMode) return;

    if (
      isSelectedDocRetrying ||
      isSelectedDocDeleting ||
      isSelectedDocRetryable ||
      selectedStatusKey === DELETE_PENDING_STATUS
    ) {
      setIsEditMode(false);
      setChunkTokenErrors({});
      setEditableChunks(
        chunks.map((chunk) => ({
          ...chunk,
          chunkText: chunk.chunkText || "",
        }))
      );
    }
  }, [
    isEditMode,
    isSelectedDocRetrying,
    isSelectedDocDeleting,
    isSelectedDocRetryable,
    selectedStatusKey,
    chunks,
  ]);

  // Size every chunk textarea to its content when the editor opens or the
  // chunks reload, so long chunks aren't crammed into a small box.
  useEffect(() => {
    if (!isEditMode) return;

    const root = chunksScrollRef.current;
    if (!root) return;

    const frame = requestAnimationFrame(() => {
      root
        .querySelectorAll("textarea.chunk-textarea")
        .forEach((el) => autoSizeTextarea(el));
    });

    return () => cancelAnimationFrame(frame);
  }, [isEditMode, chunks]);

  // ==========================================================
  // SEARCH
  // ==========================================================

  const filteredDocuments =
    documents.filter(
      (doc) => {
        const query =
          searchText.toLowerCase();

        return (
          normalizeText(
            doc.docName
          )
            .toLowerCase()
            .includes(query) ||
          normalizeText(
            doc.qdrantDocId
          )
            .toLowerCase()
            .includes(query)
        );
      }
    );

  return (
    <div
      className={`documents-page ${
        isEditMode ? "is-editing" : ""
      }`}
    >
      <div className="documents-hero">
        <div>
          <p className="documents-eyebrow">
            ChatBot Library
          </p>

          <h1>
            Documents
          </h1>

          <p className="documents-subtitle">
            Browse uploaded documents.
          </p>
        </div>

        <div className="documents-summary">
          <div className="summary-card">
            <span>
              Total Documents
            </span>

            <strong>
              {documents.length}
            </strong>
          </div>

          <div className="summary-card">
            <span>
              Selected Chunks
            </span>

            <strong>
              {chunks.length}
            </strong>
          </div>
        </div>
      </div>

      <div className="documents-layout">
        <aside className="documents-sidebar">
          <div className="panel-header">
            <div className="search-box">
              <Search size={16} />

              <input
                type="text"
                placeholder="Search document"
                value={searchText}
                onChange={(e) =>
                  setSearchText(
                    e.target.value
                  )
                }
              />
            </div>
          </div>

          {docsLoading ? (
            <div className="state-box">
              <Loader2
                className="spin"
                size={22}
              />

              <p>
                Loading documents...
              </p>
            </div>
          ) : filteredDocuments.length >
            0 ? (
            <div className="document-list">
              {filteredDocuments.map(
                (doc) => {
                  const isActive =
                    doc.qdrantDocId ===
                    selectedDocId;

                  const statusKey = (
                    doc.status || ""
                  ).toLowerCase();

                  const canRetry =
                    retryableStatuses.has(
                      statusKey
                    );

                  const isDeletePending =
                    statusKey ===
                    DELETE_PENDING_STATUS;

                  const docKey =
                    doc.qdrantDocId;

                  // Any action running for this row locks its other actions.
                  const docBusy =
                    isDocBusy(docKey);

                  // A chunk bulk-edit for this row is still in flight.
                  const docEditing =
                    isDocEditing(docKey);

                  return (
                    <div
                      key={docKey}
                      className={`document-card ${
                        isActive
                          ? "active"
                          : ""
                      }`}
                      onClick={() =>
                        setSelectedDocId(
                          doc.qdrantDocId
                        )
                      }
                      role="button"
                      tabIndex={0}
                    >
                      <div className="document-card-top">
                        <div className="document-icon">
                          <FileText size={18} />
                        </div>

                        <div className="document-card-meta">
                          <h3
                            title={
                              doc.docName
                            }
                          >
                            {doc.docName}
                          </h3>

                          <p>
                            {
                              doc.qdrantDocId
                            }
                          </p>
                        </div>

                        <ChevronRight
                          size={18}
                          className="card-chevron"
                        />
                      </div>

                      <div className="document-card-bottom">
                        <div className="document-status-wrap">
                          <span
                            className={`status-badge ${
                              statusKey ||
                              "unknown"
                            }`}
                          >
                            {formatStatusLabel(
                              doc.status
                            )}
                          </span>

                          {canRetry && (
                            <button
                              type="button"
                              className="retry-btn"
                              onClick={(
                                e
                              ) => {
                                e.stopPropagation();

                                handleRetry(
                                  doc
                                );
                              }}
                              disabled={
                                docBusy ||
                                isUploadInProgress ||
                                docEditing
                              }
                              title={
                                isUploadInProgress
                                  ? "Waiting for the current upload to finish"
                                  : docEditing
                                    ? "Waiting for the current chunk edit to finish"
                                    : undefined
                              }
                            >
                              {retryingDocId ===
                              docKey ? (
                                <>
                                  <Loader2
                                    className="spin"
                                    size={12}
                                  />

                                  Retrying
                                </>
                              ) : isUploadInProgress ||
                                docEditing ? (
                                <>
                                  <Loader2
                                    className="spin"
                                    size={12}
                                  />

                                  Processing
                                </>
                              ) : (
                                "Retry"
                              )}
                            </button>
                          )}
                        </div>

                        <span>
                          {doc.chunksCount ||
                            0}{" "}
                          chunks
                        </span>
                      </div>

                      <div className="document-card-footer">
                        <span>
                          Created{" "}
                          {formatDate(
                            doc.createdAt
                          )}
                        </span>

                        {isDeletePending ? (
                          <button
                            type="button"
                            className="retry-btn retry-delete-btn"
                            onClick={(
                              e
                            ) => {
                              e.stopPropagation();

                              handleRetryDelete(
                                doc
                              );
                            }}
                            disabled={
                              docBusy
                            }
                          >
                            {deletingDocId ===
                            docKey ? (
                              <>
                                <Loader2
                                  className="spin"
                                  size={12}
                                />

                                Retrying
                              </>
                            ) : (
                              "Retry Delete"
                            )}
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="delete-btn"
                            onClick={(
                              e
                            ) => {
                              e.stopPropagation();

                              handleDelete(
                                doc
                              );
                            }}
                            disabled={
                              docBusy ||
                              canRetry ||
                              isEditMode
                            }
                            title={
                              canRetry
                                ? "Document processing is not complete yet"
                                : undefined
                            }
                          >
                            {deletingDocId ===
                            docKey ? (
                              <Loader2
                                className="spin"
                                size={12}
                              />
                            ) : (
                              <Trash2
                                size={12}
                              />
                            )}

                            Delete
                          </button>
                        )}
                      </div>
                    </div>
                  );
                }
              )}
            </div>
          ) : (
            <div className="state-box">
              <FileSearch2 size={22} />

              <p>
                No documents found.
              </p>
            </div>
          )}
        </aside>

        <section
          className={`documents-main ${
            isEditMode ? "is-editing" : ""
          }`}
        >
          <div className="panel-header main-header">
            <div>
              <h2>
                Document Chunks
              </h2>

              <p>
                Showing chunks for the
                selected document.
              </p>
            </div>

            {selectedDocument && (
              <div className="documents-header-right">
                <div className="selected-pill">
                  <span>
                    {
                      selectedDocument.docName
                    }
                  </span>
                </div>

                <div className="documents-actions">
                  <button
                    type="button"
                    className="edit-mode-btn"
                    onClick={
                      handleToggleEditMode
                    }
                    disabled={
                      isEditModeLocked
                    }
                    title={
                      isSelectedDocRetrying
                        ? "Editing is disabled while the document is retrying"
                        : isSelectedDocDeleting
                          ? "Editing is disabled while the document is being deleted"
                          : isSelectedDocRetryable
                            ? "Editing is disabled until the document finishes processing"
                            : selectedStatusKey === DELETE_PENDING_STATUS
                              ? "Editing is disabled while deletion is pending"
                              : undefined
                    }
                  >
                    {isEditMode
                      ? "Disable edit mode"
                      : "Enable edit mode"}
                  </button>
                </div>
              </div>
            )}
          </div>

          {selectedDocument ? (
            <>
              {isEditMode && (
                <div className="edit-banner">
                  <strong>
                    Edit mode enabled.
                  </strong>

                  <span>
                    Editing {chunks.length}{" "}
                    {chunks.length === 1
                      ? "chunk"
                      : "chunks"}{" "}
                    for{" "}
                    <b>
                      {selectedDocument.docName}
                    </b>
                    .
                  </span>
                </div>
              )}

              {/* {!isEditMode && (
              <div className="document-detail-card">
                <div className="detail-item">
                  <span>
                    Document ID
                  </span>

                  <strong>
                    {
                      selectedDocument.qdrantDocId
                    }
                  </strong>
                </div>

                <div className="detail-item">
                  <span>
                    Status
                  </span>

                  <div className="status-action-wrap">
                    <strong
                      className={`status-text ${
                        (
                          selectedDocument.status ||
                          "unknown"
                        ).toLowerCase()
                      }`}
                    >
                      {formatStatusLabel(
                        selectedDocument.status
                      )}
                    </strong>

                    {retryableStatuses.has(
                      (
                        selectedDocument.status ||
                        ""
                      ).toLowerCase()
                    ) && (
                      <button
                        type="button"
                        className="retry-btn retry-btn-inline"
                        onClick={() =>
                          handleRetry(
                            selectedDocument
                          )
                        }
                        disabled={
                          isSelectedDocBusy ||
                          isUploadInProgress ||
                          isSelectedDocEditing
                        }
                        title={
                          isUploadInProgress
                            ? "Waiting for the current upload to finish"
                            : isSelectedDocEditing
                              ? "Waiting for the current chunk edit to finish"
                              : undefined
                        }
                      >
                        {retryingDocId ===
                        selectedDocument.qdrantDocId ? (
                          <>
                            <Loader2
                              className="spin"
                              size={12}
                            />

                            Retrying
                          </>
                        ) : isUploadInProgress ||
                          isSelectedDocEditing ? (
                          <>
                            <Loader2
                              className="spin"
                              size={12}
                            />

                            Processing
                          </>
                        ) : (
                          "Retry"
                        )}
                      </button>
                    )}

                    {(
                      selectedDocument.status ||
                      ""
                    ).toLowerCase() ===
                    DELETE_PENDING_STATUS ? (
                      <button
                        type="button"
                        className="retry-btn retry-btn-inline retry-delete-btn"
                        onClick={() =>
                          handleRetryDelete(
                            selectedDocument
                          )
                        }
                        disabled={
                          isSelectedDocBusy
                        }
                      >
                        {deletingDocId ===
                        selectedDocument.qdrantDocId ? (
                          <>
                            <Loader2
                              className="spin"
                              size={12}
                            />

                            Retrying
                          </>
                        ) : (
                          "Retry Delete"
                        )}
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="delete-btn delete-btn-inline"
                        onClick={() =>
                          handleDelete(
                            selectedDocument
                          )
                        }
                        disabled={
                          isSelectedDocBusy ||
                          retryableStatuses.has(
                            (
                              selectedDocument.status ||
                              ""
                            ).toLowerCase()
                          )
                        }
                        title={
                          retryableStatuses.has(
                            (
                              selectedDocument.status ||
                              ""
                            ).toLowerCase()
                          )
                            ? "Document processing is not complete yet"
                            : undefined
                        }
                      >
                        {deletingDocId ===
                        selectedDocument.qdrantDocId ? (
                          <Loader2
                            className="spin"
                            size={12}
                          />
                        ) : (
                          <Trash2
                            size={12}
                          />
                        )}

                        Delete
                      </button>
                    )}
                  </div>
                </div>

                <div className="detail-item">
                  <span>
                    Chunks
                  </span>

                  <strong>
                    {
                      selectedDocument.chunksCount ||
                      0
                    }
                  </strong>
                </div>

                <div className="detail-item">
                  <span>
                    Created
                  </span>

                  <strong>
                    {formatDate(
                      selectedDocument.createdAt
                    )}
                  </strong>
                </div>
              </div>
              )} */}

              <div
                className="chunks-scroll-area"
                ref={chunksScrollRef}
              >
                {chunksLoading ? (
                  <div className="state-box chunks-state">
                    <Loader2
                      className="spin"
                      size={24}
                    />

                    <p>
                      Loading chunks...
                    </p>
                  </div>
                ) : chunks.length > 0 ? (
                  <div className="chunks-grid">
                    {chunks.map(
                      (
                        chunk,
                        index
                      ) => {
                        const currentChunk =
                          editableChunks[
                            index
                          ] || chunk;

                        const tokenError =
                          chunkTokenErrors[
                            chunk.qdrantChunkId
                          ];

                        return (
                          <article
                            className={`chunk-card ${
                              tokenError
                                ? "chunk-card-error"
                                : ""
                            }`}
                            key={
                              chunk.qdrantChunkId
                            }
                          >
                            <div className="chunk-card-header">
                              <div>
                                <span className="chunk-label">
                                  Chunk{" "}
                                  {
                                    chunk.serialNumber
                                  }
                                </span>

                                {/* Existing page information can be added here if required. */}
                              </div>

                              <span
                                className="chunk-id"
                                title={
                                  chunk.qdrantChunkId
                                }
                              >
                                {
                                  chunk.qdrantChunkId
                                }
                              </span>
                            </div>

                            {tokenError && (
                              <div className="chunk-token-error">
                                Token limit
                                exceeded:{" "}
                                {
                                  tokenError.tokenCount
                                }

                                {tokenError.maxTokens
                                  ? `/${tokenError.maxTokens}`
                                  : ""}{" "}
                                tokens.
                                <br />

                                Please shorten
                                this chunk and
                                try again.
                              </div>
                            )}

                            {isEditMode ? (
                              <div className="chunk-edit-wrap">
                                <textarea
                                  className={`chunk-textarea ${
                                    tokenError
                                      ? "warning"
                                      : ""
                                  }`}
                                  value={
                                    currentChunk.chunkText ||
                                    ""
                                  }
                                  ref={
                                    autoSizeTextarea
                                  }
                                  onChange={(
                                    e
                                  ) => {
                                    handleChunkTextChange(
                                      index,
                                      e.target.value
                                    );

                                    autoSizeTextarea(
                                      e.target
                                    );
                                  }}
                                  rows={6}
                                />
                              </div>
                            ) : (
                              <p className="chunk-text">
                                {chunk.chunkText ||
                                  "No chunk text available."}
                              </p>
                            )}
                          </article>
                        );
                      }
                    )}
                  </div>
                ) : (
                  <div className="state-box chunks-state">
                    <FileSearch2 size={22} />

                    <p>
                      No chunks were returned
                      for this document.
                    </p>
                  </div>
                )}
              </div>

              {isEditMode && (
                <div className="edit-action-bar">
                  <button
                    type="button"
                    className="cancel-edits-btn"
                    onClick={
                      handleToggleEditMode
                    }
                    disabled={savingEdits}
                  >
                    Cancel
                  </button>

                  <button
                    type="button"
                    className="save-edits-btn"
                    onClick={
                      handleSaveBulkEdit
                    }
                    disabled={
                      savingEdits ||
                      chunksLoading ||
                      isSelectedDocBusy ||
                      editableChunks.length ===
                        0
                    }
                  >
                    {savingEdits ? (
                      <>
                        <Loader2
                          className="spin"
                          size={14}
                        />

                        Saving
                      </>
                    ) : (
                      "Save changes"
                    )}
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="state-box chunks-state">
              <FileSearch2 size={22} />

              <p>
                Select a document to see
                its chunks.
              </p>
            </div>
          )}
        </section>
      </div>

      {error && (
        <div className="documents-error">
          {error}
        </div>
      )}

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        tone="danger"
        title="Delete document?"
        message={
          <>
            Are you sure you want to delete the document <strong>{deleteTarget?.docName}</strong>? You can restore it later from the Deleted Documents tab.
          </>
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
        loading={
          Boolean(deleteTarget) &&
          deletingDocId ===
            deleteTarget?.qdrantDocId
        }
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}

export default Documents;

